/**
 * What the bag weighs, told on the bag's own icon (2026-10-07: "Can we show
 * the inventory encumbrance "fill" on the icon and show more detail by
 * hovering?", and "As part of that, don't show the numbers in the panel
 * anymore").
 *
 * The weight was a line of numbers over the bag's squares from 2026-10-04,
 * when encumbrance came back ("Remove item stacking and reintroduce
 * encumbrance. Replace the stack count helper button with invert"). It
 * is the icon now: a fill rising from the icon's foot as the bag gets
 * heavier, and the numbers on its hover and its long press. The icon is
 * on screen whether the panel is open or shut, which the line never was.
 *
 * Kept apart from main.ts, which draws it, so that the arithmetic and the
 * words can be run under node and checked without a page.
 */

import { num } from "./numfmt";

/**
 * A weight in coins, grouped in thousands. The numbers run to five and six
 * figures — a coin weighs one coin, and people carry a great many coins —
 * and "103400" is a number nobody reads at a glance. Back since 2026-10-04
 * with the weight itself: "Remove item stacking and reintroduce
 * encumbrance. Replace the stack count helper button with invert". The
 * grouping was a thin space whatever the reader's language until
 * 2026-10-13, and is now theirs, the same as every other number the client
 * writes (numfmt.ts). The look the server writes still uses the thin space
 * (fmtCn in itemfacts.go), since the server does not know the reader's
 * language.
 */
export function fmtCn(cn: number): string {
  return num(cn);
}

/**
 * How the fill is coloured: muted while there is room, amber from
 * HEAVY_AT of the way up, and red past the line. It took no colour as a
 * line of numbers, so as not to pull the eye in a fight; on the icon the
 * colour is the reading, and amber is a nudge towards the depot that
 * comes well before the refusal does.
 */
export type BagLoad = "light" | "heavy" | "over";

/** Where the fill turns amber: four fifths of what can be lifted. */
export const HEAVY_AT = 0.8;

export interface BagFill {
  /** How far up the icon the fill stands, 0 to 1: never past the top. */
  fill: number;
  /** The weight as a whole percent of what can be lifted, uncapped. */
  percent: number;
  load: BagLoad;
}

/**
 * The fill for a bag of cn coins' weight of the maxcn this character can
 * lift. Over is anything past the line, and only that: at exactly the line
 * the bag is full but not over, since the server lets you up to it
 * (canCarry is "at most", in world.go).
 */
export function bagFill(cn: number, maxcn: number): BagFill {
  if (!(maxcn > 0)) return { fill: 0, percent: 0, load: "light" };
  const frac = Math.max(0, cn) / maxcn;
  return {
    fill: Math.min(1, frac),
    percent: Math.floor(frac * 100),
    load: cn > maxcn ? "over" : frac >= HEAVY_AT ? "heavy" : "light",
  };
}

/**
 * The words for the hover and the long press: the numbers, and what the
 * weight does. What it does is what the server does and no more. Weight
 * never slows anybody — the step is the level's, the race's and the
 * spells' (stepMsOf, in spells.go) — and it takes nothing out of a bag
 * (canCarry). All it does is refuse: whatever would take you past the line
 * is left where it is, whole, and the log says the numbers. Over the line,
 * that is everything with any weight at all, until you are back under it;
 * a sale or a trade that leaves you lighter still goes through (sell, in
 * shop.go, and the trade's close, in trade.go), since that is the way back.
 */
export function bagWeightTip(cn: number, maxcn: number): string {
  const { percent, load } = bagFill(cn, maxcn);
  const have = `${fmtCn(cn)} / ${fmtCn(maxcn)} cn (${percent}%)`;
  if (load === "over") {
    return (
      `Weight: ${have}, ${fmtCn(cn - maxcn)} cn over what you can carry. ` +
      "You keep all of it and walk as fast as ever, but nothing more goes into your bag until you are back under the line: " +
      "drop, sell or store something. A sale or a trade that leaves you lighter still goes through. A gold coin is 1 cn."
    );
  }
  const free = maxcn - cn;
  const room =
    free === 0
      ? "Full: anything that weighs something is left where it is"
      : `${fmtCn(free)} cn free${load === "heavy" ? ", nearly full" : ""}. Anything that would take you past ${fmtCn(maxcn)} is left where it is`;
  return `Weight: ${have}. ${room}, and the log says the numbers. Weight never slows you. A gold coin is 1 cn.`;
}
